<template>
    <div>
        <x-layout v-if="showCaptiveInsurer">
            <x-flex xs12 >
                <x-select
                    :items="[captiveInsurer]"
                    item-text="name"
                    item-value="id"
                    :value="captiveInsurer.id"
                    label="Choose captive insurer"
                    placeholder="Captive insurer"
                    append-icon="keyboard_arrow_down"
                />
            </x-flex>
        </x-layout>

        <x-layout>
            <x-flex xs12>
                <x-select
                    ref="insurers"
                    v-model="selectedInsurers"
                    :items="insurersItems"
                    class="required"
                    label="Choose insurer(s)"
                    placeholder="Insurers"
                    item-text="name"
                    item-value="id"
                    append-icon="keyboard_arrow_down"
                    multiple
                    :rules="rules"
                    :menu-props="{
                        offsetY: true,
                        maxHeight: 400
                    }"
                >
                    <template slot="selection" slot-scope="{ item, index }">
                        <span v-if="index === 0">{{ selectedInsurers.length }} insurers selected</span>
                    </template>

                    <template slot="prepend-item">
                        <x-list-tile class="buttons-top-tile no-hover">
                            <x-btn @click="selectAllInsurers" color="secondary">Select all</x-btn>
                            <x-btn @click="deselectAllInsurers" color="tertiary">Deselect all</x-btn>
                        </x-list-tile>
                    </template>
                </x-select>
            </x-flex>
        </x-layout>

        <x-layout v-if="filteredSelectedInsurersList.length" class="nested mt-3">
            <x-flex
                xs12
                :class="{ 'insurer-list': filteredSelectedInsurersList.length }"
            >
                <x-expansion-panel>
                    <x-expansion-panel-content
                        v-for="insurer in filteredSelectedInsurersList"
                        :key="insurer.id"
                    >
                        <template v-slot:header>
                            <div class="insurer-list-title">{{ insurer.name }}</div>

                            <div class="insurer-list-count">
                                <span>{{ getInsurerSelectedUserCount(insurer) }}</span> {{ getInsurerSelectedUserCount(insurer) === 1 ? 'user' : 'users' }} selected
                            </div>
                        </template>

                        <x-card>
                            <x-data-table :items="insurer.users" class="no-row-hover" hide-actions>
                                <template v-slot:items="props">
                                    <tr @click="updateInsurerUsers(props.item.id, props.item.company_id)">
                                        <td>
                                            <x-checkbox
                                                :input-value="isSelected(props.item.id, props.item.company_id)"
                                                :label="props.item.name"
                                                hide-details
                                            />
                                        </td>
                                    </tr>
                                </template>
                            </x-data-table>
                        </x-card>

                        <p class="error--text text-xs-center ma-3" :ref="'insurer-' + insurer.id"/>
                    </x-expansion-panel-content>
                </x-expansion-panel>
            </x-flex>
        </x-layout>
    </div>
</template>

<script>
    import CheckIcon from '~/components/Shared/UIComponents/CheckIcon';
    import rules from '~/mixins/rules';

    export default {
        name: 'AddInsurers',
        components: {
            CheckIcon
        },
        mixins: [rules],
        props: {
            insurers: {},
            defaultInsurerIds: {
                type: Array,
                default: () => []
            },
            mandatoryInsurers: {
                type: Array,
                default: () => []
            },
            selectedInsurersUsers: {
                type: Object,
                default: () => {}
            },
            rules: {
                type: Array,
                default ()  {
                   return [v => this.ruleMinSelected(v, 1, false, 'At least 1 insurer must be selected')]
                }
            },
            captiveInsurer: {
                type: Object,
                default: () => {}
            },
        },
        data() {
            return {
                selectedInsurers: [],
                loading: true,
            }
        },
        computed: {
            filteredInsurers() {
                return this.insurers.filter(insurer => insurer.users.length > 0);
            },
            filteredSelectedInsurersList() {
                return this.filteredInsurers.filter(insurer => this.selectedInsurers.includes(insurer.id));
            },
            insurersItems() {
                return this.filteredInsurers.map((insurer) => {
                    if (this.mandatoryInsurers.includes(insurer.id)) {
                        insurer['disabled'] = true;
                    }
                    return insurer;
                })
            },
            showCaptiveInsurer() {
                return this.captiveInsurer && Object.keys(this.captiveInsurer).length > 0;
            }
        },
        async mounted() {
            this.loading = false;
            //filter only insurers not captive
            const mandatoryInsurersIds = this.insurers.filter(insurer => this.isCompanyTypeInsurer(insurer) && this.mandatoryInsurers.includes(insurer.id)).map(insurer => insurer.id);
            const defaultInsurerIds = this.insurers.filter(insurer => this.isCompanyTypeInsurer(insurer) && this.defaultInsurerIds.includes(insurer.id) && !mandatoryInsurersIds.includes(insurer.id)).map(insurer => insurer.id);

            if (mandatoryInsurersIds.length > 0 || defaultInsurerIds.length > 0) {
                this.selectedInsurers = [...mandatoryInsurersIds, ...defaultInsurerIds];
            }

            if (Object.keys(this.selectedInsurersUsers).length > 0) {
                this.selectedInsurers = [...Object.keys(this.selectedInsurersUsers).map(id => parseInt(id))];
            }
        },
        watch: {
            selectedInsurers: {
                handler(newInsurerIds, oldInsurerIds) {
                    newInsurerIds.forEach((insurerId) => {
                        if (!this.selectedInsurersUsers.hasOwnProperty(insurerId)) {
                            let insurerUsers = this.filteredInsurers
                                .find((insurer) => insurer.id === insurerId)
                                .users.map(users => users.id);
                            this.$set(this.selectedInsurersUsers, insurerId, insurerUsers);
                        }
                    });
                    if (oldInsurerIds) {
                        oldInsurerIds.forEach(insurerId => {
                            if (!newInsurerIds.includes(insurerId)) {
                                this.$delete(this.selectedInsurersUsers, insurerId);
                            }
                        });
                    }
                },
                immediate: true
            },
            selectedInsurersUsers: {
                handler() {
                    this.selectedInsurers = [...Object.keys(this.selectedInsurersUsers).map(id => parseInt(id))];
                    this.$emit('update:selectedInsurersUsers', this.selectedInsurersUsers);
                },
                deep: true,
                immediate: true
            },
        },
        methods: {
            updateSelectMenuDimensions() {
                this.$refs.insurers.$refs.menu.onResize();
            },
            selectAllInsurers() {
                this.selectedInsurers = this.filteredInsurers.map(insurer => insurer.id);
                this.updateSelectMenuDimensions();
            },
            deselectAllInsurers() {
                this.selectedInsurers = this.mandatoryInsurers;
                this.updateSelectMenuDimensions();
            },
            addErrorMessage(companyId) {
                this.$refs['insurer-' + companyId][0].innerText = 'At least 1 user must be selected';

                setTimeout(() => {
                    this.$refs['insurer-' + companyId][0].innerText = ''
                }, 1500);
            },
            getInsurerSelectedUserCount(insurer) {
                let userCount = 0;

                if (this.selectedInsurersUsers.hasOwnProperty(insurer.id)) {
                    userCount = this.selectedInsurersUsers[insurer.id].length === insurer.users.length
                        ? 'All' : this.selectedInsurersUsers[insurer.id].length;
                }

                return userCount;
            },
            isSelected(userId, companyId) {
                return this.selectedInsurersUsers.hasOwnProperty(companyId) &&
                    this.selectedInsurersUsers[companyId].includes(userId);
            },
            updateInsurerUsers(userId, companyId) {
                if (this.isSelected(userId, companyId)) {
                    if (this.selectedInsurersUsers[companyId].length > 1) {
                        this.selectedInsurersUsers[companyId].splice(this.selectedInsurersUsers[companyId].indexOf(userId), 1);
                    } else {
                        this.addErrorMessage(companyId);
                    }
                } else {
                    this.$set(this.selectedInsurersUsers, companyId, [userId, ...this.selectedInsurersUsers[companyId]]);
                }
            },
            isCompanyTypeInsurer(insurer) {
                return insurer.company_type === 'insurer';
            }
        }
    }
</script>

<style lang="scss" scoped>
    div.container.step-form {
        margin-top: 66px;
    }

    .x-select-list :deep() {
        .x-list {
            .buttons-top-tile {
                .x-list__tile {
                    justify-content: flex-end;
                    margin: -7px -7px 0;
                    padding-top: 0;
                    padding-bottom: 0;

                    button {
                        flex-grow: 0;
                        width: 120px;
                    }
                }
            }
        }
    }
    .insurer-list :deep(){
        padding: 0 0 0 10px;

        .x-expansion-panel {
            list-style: none !important;

            .x-expansion-panel__container{
                margin: 0 0 10px 0;
                border: none !important;
                background: transparent !important;
                box-shadow: none;

                &:before {
                    content: none;
                }

                &:last-child {
                    margin-bottom: 0;
                }

                .x-expansion-panel__header {
                    padding: 0 16px !important;
                    border-bottom: 1px solid rgba(255, 255, 255, 0.25);

                    .insurer-list-title {
                        font-family: $font-title;
                        font-size: 18px;
                        color: $color-pistachio;
                    }

                    .insurer-list-count {
                        margin-right: 12px;
                        font-size: 16px;
                        text-align: right;
                        color: $color-teal;

                        span {
                            text-decoration: underline;
                        }
                    }

                    .x-expansion-panel__header__icon {
                        background: none;
                    }
                }

                .x-expansion-panel__body{
                    padding: 0 !important;
                    border-left: 1px solid rgba(255, 255, 255, 0.25);

                    .x-card {
                        padding: 0;
                        background: transparent !important;
                        border-color: transparent;
                    }
                }
            }

            table.x-table{
                thead {
                    display: none;
                }

                tr {
                    cursor: pointer;
                    height: 40px;

                    .x-input--checkbox {
                        .x-input__slot {
                            flex-direction: row-reverse;

                            .x-input--selection-controls__input {
                                height: 20px;
                                width: 20px;
                                margin-right: 12px;
                            }

                            .x-input--selection-controls__check {
                                height: 20px;
                                width: 20px;
                            }
                        }
                    }
                }
            }
        }
    }
</style>
